import type { UIElementAdapter, UIElementRef, ActivateResult } from '@codenexus/react';

interface UICallbacks {
  [elementId: string]: (() => void) | undefined;
}

/**
 * Automatic UI Element Adapter for Intelligenic application
 * Discovers UI elements automatically via data-ui-bridge attributes
 * Zero manual registration required - just add data attributes to UI elements
 */
export class InteligenicUIAdapter implements UIElementAdapter {
  private listeners: Array<(refs: UIElementRef[]) => void> = [];
  private observer: MutationObserver | null = null;
  private cachedElements: UIElementRef[] = [];
  private scanScheduled = false;

  constructor(private callbacks: UICallbacks = {}) {
    this.startObserving();
    // Initial scan
    this.scanDOM();
    // Re-scan after a short delay to catch elements that render async
    setTimeout(() => this.scanDOM(), 100);
  }

  /**
   * Coalesce burst-mutations from a single render pass into one scan.
   * Without this, a single React update can trigger dozens of mutations,
   * each scheduling its own scan.
   */
  private scheduleScan(): void {
    if (this.scanScheduled) return;
    this.scanScheduled = true;
    queueMicrotask(() => {
      this.scanScheduled = false;
      this.scanDOM();
    });
  }

  /**
   * Scans DOM for elements with data-ui-bridge attributes and builds the
   * registry. Only notifies listeners when the registry actually changed
   * — otherwise an idle render burst would loop subscribers forever.
   */
  private scanDOM(): boolean {
    const elements = document.querySelectorAll('[data-ui-bridge]');
    const next = Array.from(elements).map(el => this.elementToRef(el as HTMLElement));
    const changed = !this.elementsEqual(this.cachedElements, next);

    if (!changed) {
      return false;
    }

    if (changed) {
      this.cachedElements = next;
      console.log(`[InteligenicUIAdapter] UI elements updated: ${next.length} available`, next.map(e => e.id));
    }

    this.emitChange();
    return changed;
  }

  private elementsEqual(a: UIElementRef[], b: UIElementRef[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      const x = a[i], y = b[i];
      if (
        x.id !== y.id ||
        x.label !== y.label ||
        x.role !== y.role ||
        x.group !== y.group ||
        x.enabled !== y.enabled ||
        x.visible !== y.visible ||
        x.description !== y.description
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Converts a DOM element to a UIElementRef
   */
  private elementToRef(el: HTMLElement): UIElementRef {
    const id = el.getAttribute('data-ui-bridge') || '';
    const label = el.getAttribute('data-ui-label') || el.textContent?.trim() || id;
    const role = (el.getAttribute('data-ui-role') || 'button') as UIElementRef['role'];
    const group = el.getAttribute('data-ui-group') || 'general';
    const description = el.getAttribute('data-ui-description') || `Activate ${label}`;

    // Check if element is visible and enabled
    const style = window.getComputedStyle(el);
    const visible = style.display !== 'none' && style.visibility !== 'hidden';
    const enabled = !el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true';

    return {
      id,
      label,
      role,
      group,
      enabled,
      visible,
      description,
    };
  }

  /**
   * Starts observing DOM changes to detect new/removed UI elements
   */
  private startObserving(): void {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    this.observer = new MutationObserver(() => {
      this.scheduleScan();
    });

    // Only watch attributes that actually affect the UI-bridge registry.
    // Previously included 'style' and 'class' — those fire on every MUI
    // emotion update, hover transition, and React render, which is the
    // source of an infinite render loop in subscribers.
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-ui-bridge',
        'data-ui-label',
        'data-ui-role',
        'data-ui-group',
        'data-ui-description',
        'disabled',
        'aria-disabled',
      ],
    });
  }

  /**
   * Stops observing DOM changes
   */
  private stopObserving(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  listElements(): UIElementRef[] {
    // Keep list_ui_elements context fresh even if mutation observers missed
    // a render boundary.
    this.scanDOM();
    return this.cachedElements;
  }

  async activate(id: string): Promise<ActivateResult> {
    // Find the DOM element
    const el = document.querySelector(`[data-ui-bridge="${id}"]`) as HTMLElement;

    if (!el) {
      return {
        status: 'failed',
        reason: 'not_found',
        message: `UI element '${id}' not found in DOM`
      };
    }

    // Check if disabled
    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') {
      return {
        status: 'failed',
        reason: 'disabled',
        message: `UI element '${id}' is currently disabled`
      };
    }

    // Try registered callback first
    const callback = this.callbacks[id];
    if (callback) {
      try {
        callback();
        return { status: 'activated' };
      } catch (error) {
        return {
          status: 'failed',
          reason: 'host_error',
          message: `Callback failed: ${error instanceof Error ? error.message : String(error)}`
        };
      }
    }

    // Fallback to triggering click event
    try {
      el.click();
      return { status: 'activated' };
    } catch (error) {
      return {
        status: 'failed',
        reason: 'host_error',
        message: `Click failed: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  onChange(listener: (refs: UIElementRef[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Manually trigger a scan. Listeners are notified only when the registry
   * changed, matching mutation-observer scans and preventing duplicate UI
   * snapshots from navigation effects that report the same rendered state.
   */
  notifyChange(): void {
    this.scanDOM();
  }

  /**
   * Emit the currently-cached registry to subscribers.
   */
  private emitChange(): void {
    this.listeners.forEach(l => l(this.cachedElements));
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopObserving();
    this.listeners = [];
    this.cachedElements = [];
  }
}
